import { ArrowUpRight, ExternalLink, Lock, Rocket } from "lucide-react";

const projects = [
    {
        name: "ezScribe",
        url: "https://ezscribe.net",
        tagline: "AI Medical Scribe & Transcription",
        description:
            "HIPAA-compliant scribe that turns patient conversations into structured SOAP notes with ICD-10 codes — in seconds. Bilingual EN/ES, works on any device.",
        tags: ["Custom Agent", "Full-Stack Web App", "Healthcare"],
        // Drop a real screenshot at /app/frontend/public/showcase/ezscribe.jpg
        // and uncomment the line below to enable it.
        screenshot: null,
        poster: {
            label: "AI Medical Scribe",
            stat: "$49",
            statLabel: "/ month flat",
            highlights: ["HIPAA Compliant", "Bilingual EN/ES", "ICD-10 Auto-Coding"],
            accent: "#0ea5e9",
        },
        live: true,
    },
    {
        name: "ezItemizer",
        url: "https://ezitemizer.com",
        tagline: "Industrial Parts Inventory",
        description:
            "Inventory control system built for trailer manufacturers. Real stock counts, role-aware access, low-stock alerts, and full audited movement history — purpose-built for the floor.",
        tags: ["Full-Stack Web App", "Manufacturing", "Internal Tool"],
        // Drop a real screenshot at /app/frontend/public/showcase/ezitemizer.jpg
        screenshot: null,
        poster: {
            label: "Inventory Control System",
            stat: "v2.0",
            statLabel: "trailer manufacturing",
            highlights: ["Unlimited SKUs", "3 Role Tiers", "Full Audit Log"],
            accent: "#f59e0b",
        },
        live: true,
    },
    {
        name: "Prompt Studio",
        url: "https://simpleailife.gadgetguymedia.com",
        tagline: "AI Prompt Toolkit",
        description:
            "A complete AI prompt toolkit — browse 50+ expert prompt examples, build custom prompts with a guided builder, and test them live in an interactive playground. Master the prompts that unlock any AI chatbot.",
        tags: ["AI Tool", "Prompt Engineering", "Free SaaS"],
        screenshot: null,
        poster: {
            label: "AI Prompt Toolkit",
            stat: "Free",
            statLabel: "100% free to use",
            highlights: ["50+ Prompt Library", "Guided Builder", "Live Playground"],
            accent: "#34d399",
        },
        live: true,
    },
];

export default function Showcase() {
    return (
        <section
            id="showcase"
            data-testid="showcase-section"
            className="relative py-24 sm:py-32 scroll-mt-nav overflow-hidden"
        >
            <div className="absolute top-0 right-1/4 w-[500px] h-[500px] rounded-full bg-[#00f0ff]/[0.03] blur-[140px] pointer-events-none" />
            <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] rounded-full bg-[#1d4ed8]/[0.08] blur-[140px] pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                    <div className="max-w-2xl">
                        <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#00f0ff]">
                            See what we build
                        </span>
                        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mt-4 leading-[1.05]">
                            Real apps.{" "}
                            <span className="text-gradient-cyan">Live in production.</span>
                        </h2>
                    </div>
                    <p className="text-[#94a3b8] md:max-w-sm">
                        Every project below was scoped, built, and deployed by the same small
                        team that&apos;ll work on yours. No subcontractors. No handoffs.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                    {projects.map((p, i) => (
                        <ShowcaseCard key={i} project={p} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function ShowcaseCard({ project, index }) {
    const { name, url, tagline, description, tags, screenshot, poster, live } = project;

    const Wrapper = live ? "a" : "div";
    const wrapperProps = live
        ? { href: url, target: "_blank", rel: "noopener noreferrer" }
        : {};

    return (
        <Wrapper
            {...wrapperProps}
            data-testid={`showcase-card-${index + 1}`}
            className={`group relative rounded-2xl bg-[#0a1325] border border-white/10 overflow-hidden transition-all duration-500 ${
                live
                    ? "hover:border-[#00f0ff]/50 hover:-translate-y-1 cursor-pointer block"
                    : "opacity-90"
            }`}
        >
            {/* Screenshot frame */}
            <div className="relative aspect-[16/10] bg-[#050b14] overflow-hidden border-b border-white/5">
                {/* Browser chrome */}
                <div className="absolute top-0 inset-x-0 h-7 bg-[#050b14]/95 border-b border-white/5 flex items-center gap-1.5 px-3 z-20">
                    <span className="w-2 h-2 rounded-full bg-red-500/60" />
                    <span className="w-2 h-2 rounded-full bg-yellow-500/60" />
                    <span className="w-2 h-2 rounded-full bg-green-500/60" />
                    {url && (
                        <span className="ml-3 text-[10px] font-mono text-[#475569] truncate">
                            {url.replace(/^https?:\/\//, "")}
                        </span>
                    )}
                </div>

                {screenshot ? (
                    <img
                        src={screenshot}
                        alt={`${name} screenshot`}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover object-top pt-7 transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                ) : poster ? (
                    <PosterPreview poster={poster} name={name} />
                ) : (
                    <ComingSoonGraphic />
                )}

                {screenshot && (
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050b14]/40 via-transparent to-transparent pointer-events-none" />
                )}

                {live && (
                    <div className="absolute top-9 right-3 z-20 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#050b14]/80 backdrop-blur-sm border border-[#00f0ff]/30 text-[10px] font-bold uppercase tracking-[0.18em] text-[#00f0ff]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#00f0ff] animate-pulse" />
                        Live
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="p-6 sm:p-7">
                <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-heading text-xl font-bold tracking-tight text-white">
                        {name}
                    </h3>
                    {live ? (
                        <ArrowUpRight className="w-5 h-5 text-[#94a3b8] group-hover:text-[#00f0ff] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0" />
                    ) : (
                        <Lock className="w-4 h-4 text-[#475569] shrink-0 mt-1" />
                    )}
                </div>
                <p className="text-xs uppercase tracking-wider text-[#00f0ff]/80 font-bold mb-3">
                    {tagline}
                </p>
                <p className="text-sm text-[#94a3b8] leading-relaxed mb-5">
                    {description}
                </p>
                <div className="flex flex-wrap gap-1.5">
                    {tags.map((tag) => (
                        <span
                            key={tag}
                            className="px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-[10px] font-mono uppercase tracking-wider text-[#cbd5e1]"
                        >
                            {tag}
                        </span>
                    ))}
                </div>
                {live && url && (
                    <div className="mt-5 pt-5 border-t border-white/5 flex items-center gap-1.5 text-xs font-medium text-[#94a3b8] group-hover:text-[#00f0ff] transition-colors">
                        <ExternalLink className="w-3.5 h-3.5" />
                        Visit live site
                    </div>
                )}
            </div>
        </Wrapper>
    );
}

function PosterPreview({ poster, name }) {
    const accent = poster.accent || "#00f0ff";
    return (
        <div className="absolute inset-0 pt-7 overflow-hidden">
            <div
                className="absolute inset-0"
                style={{
                    background: `radial-gradient(ellipse at 30% 20%, ${accent}33 0%, transparent 55%), radial-gradient(ellipse at 80% 90%, #1d4ed833 0%, transparent 60%), #050b14`,
                }}
            />
            <div className="absolute inset-0 grid-bg opacity-30" />
            <div className="relative h-full flex flex-col justify-between p-7">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/60 mb-2">
                        {poster.label}
                    </div>
                    <div className="font-heading text-3xl font-black tracking-tighter text-white leading-none">
                        {name}
                    </div>
                </div>
                <div>
                    <div className="flex items-baseline gap-1.5 mb-3">
                        <span className="font-heading text-4xl font-black tracking-tighter text-white">
                            {poster.stat}
                        </span>
                        <span className="text-xs text-white/50">{poster.statLabel}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {poster.highlights.map((h) => (
                            <span
                                key={h}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/[0.06] border border-white/10 text-[10px] text-white/80"
                            >
                                <span
                                    className="w-1 h-1 rounded-full"
                                    style={{ background: accent }}
                                />
                                {h}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function ComingSoonGraphic() {
    return (
        <div className="absolute inset-0 pt-7 flex items-center justify-center">
            <div className="absolute inset-0 grid-bg opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#00f0ff]/5 via-transparent to-[#1d4ed8]/10" />
            <div className="relative text-center px-6">
                <div className="inline-flex w-14 h-14 rounded-2xl bg-[#0a1325] border border-[#00f0ff]/30 items-center justify-center mb-4 mx-auto">
                    <Rocket className="w-6 h-6 text-[#00f0ff]" />
                </div>
                <p className="font-heading text-sm font-semibold text-white tracking-wide">
                    In Production
                </p>
                <p className="text-xs text-[#64748b] mt-1">Case study coming soon</p>
            </div>
        </div>
    );
}
